import React, { useState } from 'react';
import { 
  Typography, 
  Box, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Card,
  Grid,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { currentYear, reenrolmentYear } from '../../utils/years';

const TermsandConditions = () => {
  const currentyear = currentYear();
  const nextyear = reenrolmentYear();
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ mt: 4, mb: 4, width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
        Terms and Conditions
      </Typography>
      <Typography sx={{ color: '#64748b', fontSize: '14px', mb: 3 }}>
        Please review the key summary and detailed policies before proceeding with your re-enrolment.
      </Typography>

      {/* Key Summary Section */}
      <Card sx={{ 
        mb: 4, 
        border: '1px solid #e2e8f0', 
        borderRadius: '16px', 
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' 
      }}>
        <Box sx={{ 
          bgcolor: '#f8fafc', 
          p: 2.5, 
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>
            Key Summary View
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={`EBD: Pay by 30 Nov ${currentyear}`} 
              size="small" 
              sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 600, borderRadius: '6px' }} 
            />
          </Box>
        </Box>
        
        <Box sx={{ p: 3 }}>
          <Box sx={{ 
            background: 'linear-gradient(to right, #fee2e2, #fef2f2)', 
            borderLeft: '4px solid #ef4444',
            p: 2,
            mb: 3,
            borderRadius: '0 8px 8px 0'
          }}>
            <Typography sx={{ color: '#991b1b', fontWeight: 600, mb: 0.5, fontSize: '15px' }}>
              Important Fee Policy
            </Typography>
            <Typography sx={{ color: '#991b1b', fontSize: '14px' }}>
              Deposit Fee (Re-Enrolment Fee) is strictly non-refundable. Discounts apply ONLY to School Fees.
            </Typography>
          </Box>

          <Typography sx={{ fontWeight: 600, color: '#334155', mb: 1.5, fontSize: '14px' }}>
            School Fees DO NOT Include:
          </Typography>
          <Grid container spacing={1.5}>
            {['Bus Fee', 'Intercollege Shuttle', 'Resource Fee', 'VET Subject / Academy Levies'].map((item, idx) => (
              <Grid item xs={6} sm={3} key={idx}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: '#f1f5f9', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>
                    {item}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Card>

      {/* Accordion Policies */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontWeight: 600, color: '#1e293b', mb: 2, fontSize: '16px' }}>
          Detailed Policies
        </Typography>

        <Accordion 
          expanded={expanded === 'panel1'} 
          onChange={handleChange('panel1')}
          sx={{ '&:before': { display: 'none' }, border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, boxShadow: 'none' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: expanded === 'panel1' ? '#f8fafc' : 'white', borderRadius: '12px' }}>
            <Typography sx={{ fontWeight: 600, color: '#334155' }}>1. Early Bird Discount (EBD) Rules</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ bgcolor: 'white', pt: 0, pb: 2, px: 2.5 }}>
            <Box component="ul" sx={{ pl: 2, color: '#475569', fontSize: '14px', '& li': { mb: 1 } }}>
              <li>Available for all Year Groups if full payment (including Resource Fees) is made before 30th November {currentyear}.</li>
              <li>Parents receiving the Health Care Card (HCC) Discount are no longer eligible for the Early Bird Discount.</li>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'panel2'} 
          onChange={handleChange('panel2')}
          sx={{ '&:before': { display: 'none' }, border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, boxShadow: 'none' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: expanded === 'panel2' ? '#f8fafc' : 'white', borderRadius: '12px' }}>
            <Typography sx={{ fontWeight: 600, color: '#334155' }}>2. AIC Staff Discounts Rules</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ bgcolor: 'white', pt: 0, pb: 2, px: 2.5 }}>
            <Box component="ul" sx={{ pl: 2, color: '#475569', fontSize: '14px', '& li': { mb: 1 } }}>
              <li>If the Staff Discount is applicable, no other discounts can be used.</li>
              <li>Applicable only to current employees of AIC. To retain full benefit for {nextyear}, employment must remain active for the full calendar year. If employment ends, the benefit will be prorated.</li>
              <li>Year 11 and Year 12 students are eligible for the AIC Staff Discount; however, the applicable discount percentage differs from the discount provided to students from Pre-Primary to Year 10.</li>
              <li>Kindy students are not eligible for the AIC Staff Discount. However, staff members may select the 5% EBD option for their Kindy students if the full-year school fees are paid by 30 November {currentyear}.</li>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'panel3'} 
          onChange={handleChange('panel3')}
          sx={{ '&:before': { display: 'none' }, border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, boxShadow: 'none' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: expanded === 'panel3' ? '#f8fafc' : 'white', borderRadius: '12px' }}>
            <Typography sx={{ fontWeight: 600, color: '#334155' }}>3. Health Care Card (HCC)</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ bgcolor: 'white', pt: 0, pb: 2, px: 2.5 }}>
            <Box component="ul" sx={{ pl: 2, color: '#475569', fontSize: '14px', '& li': { mb: 1 } }}>
              <li>Holders of a valid HCC card with the student’s name listed on the card are eligible for a 5% discount on school fees. However, the HCC discount cannot be combined with the EBD discount. Only one of these discounts can be applied.</li>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'panel4'} 
          onChange={handleChange('panel4')}
          sx={{ '&:before': { display: 'none' }, border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, boxShadow: 'none' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: expanded === 'panel4' ? '#f8fafc' : 'white', borderRadius: '12px' }}>
            <Typography sx={{ fontWeight: 600, color: '#334155' }}>4. Payment Schedules & Account Rules</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ bgcolor: 'white', pt: 0, pb: 2, px: 2.5 }}>
            <Box component="ul" sx={{ pl: 2, color: '#475569', fontSize: '14px', '& li': { mb: 1 } }}>
              <li>Fees for Year 9, Year 10, Year 11, and Year 12 must be paid in full by Term 3, Week 2, {nextyear}.</li>
              <li>Discounts and calculations are only applicable to presently enrolled students. For queries, contact Admin Staff.</li>
              <li>In case of payment delays due to bank processing (e.g., payments not received by AIC), the college reserves the right to contact you later to verify.</li>
              <li>If Accounts Receivable cannot reach you regarding missing payments, AIC may terminate your re-enrolment.</li>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Formal Legal Declaration Banner */}
      <Box sx={{ 
        bgcolor: '#f1f5f9', 
        p: 3, 
        borderRadius: '12px', 
        border: '1px solid #cbd5e1' 
      }}>
        <Typography sx={{ fontWeight: 700, color: '#334155', mb: 1.5, fontSize: '15px' }}>
          Parent / Guardian Declaration
        </Typography>
        <Typography sx={{ color: '#475569', fontSize: '13px', lineHeight: 1.6, mb: 1 }}>
          I understand that providing incorrect information may lead to the revocation of my application by the College in the future.
        </Typography>
        <Typography sx={{ color: '#475569', fontSize: '13px', lineHeight: 1.6 }}>
          By completing my Re-Enrolment application, I am agreeing to the Terms and Conditions of Re-Enrolment set forth by the College, including a commitment to pay all relevant fees and levies, and will abide by the College's School Fees Policy. I acknowledge that this online Re-Enrolment does not require a manual signature.
        </Typography>
      </Box>
    </Box>
  );
};

export default TermsandConditions;
